import { addDays, busyIntervals, collectItems, Temporal, timingInstants, todayIn, type AppState, type DisplayItem, type Timing } from '@autoday/core';

const MINUTE_MS = 60_000;
const MIN_GAP_MS = 15 * MINUTE_MS;

export interface NextStep {
  today: string;
  current: DisplayItem[];
  next: DisplayItem[];
  minutesUntilNext: number | null;
  /** Whole minutes in remaining calendar gaps of at least 15 minutes, up to local midnight. */
  freeMinutes: number;
  firstGap: { start: number; end: number } | null;
  hiddenCalendarsCounted: number;
  uncertainCount: number;
}

function reversedTiming(timing: Timing): boolean {
  return timing.kind === 'allDay' ? timing.endExclusive <= timing.start : timing.end < timing.start;
}

/** Read-only summary. Actionable rows use visible calendars; availability uses every busy calendar. */
export function nextStep(state: AppState, now: Temporal.Instant): NextStep {
  const tz = state.settings.timeZone;
  const today = todayIn(tz, now).toString();
  const dayStart = Temporal.PlainDate.from(today).toZonedDateTime({ timeZone: tz }).epochMilliseconds;
  const dayEnd = Temporal.PlainDate.from(addDays(today, 1)).toZonedDateTime({ timeZone: tz }).epochMilliseconds;
  const nowMs = now.epochMilliseconds;
  const result: NextStep = {
    today, current: [], next: [], minutesUntilNext: null, freeMinutes: 0, firstGap: null,
    hiddenCalendarsCounted: 0, uncertainCount: 0,
  };

  try {
    // Local recurrence dates may be two dates ahead of the display day at extreme zone offsets.
    const items = collectItems(state, addDays(today, -2), addDays(today, 3));
    const timed: Array<{ item: DisplayItem; start: number; end: number }> = [];
    let malformed = false;
    for (const item of items) {
      const { start, end } = timingInstants(item.timing, tz);
      const startMs = start.epochMilliseconds;
      const endMs = end.epochMilliseconds;
      if (endMs < startMs || (item.timing.kind === 'allDay' && endMs === startMs)) malformed = true;
      if (item.done || item.timing.kind !== 'timed' || endMs <= startMs) continue;
      if (startMs < dayEnd && endMs > dayStart) timed.push({ item, start: startMs, end: endMs });
    }
    timed.sort((a, b) => a.start - b.start || a.end - b.end || a.item.uid.localeCompare(b.item.uid));
    result.current = timed.filter((entry) => entry.start <= nowMs && nowMs < entry.end).map((entry) => entry.item);
    const nextStart = timed.find((entry) => entry.start > nowMs)?.start;
    if (nextStart !== undefined) {
      result.next = timed.filter((entry) => entry.start === nextStart).map((entry) => entry.item);
      result.minutesUntilNext = Math.ceil((nextStart - nowMs) / MINUTE_MS);
    }

    const availability = busyIntervals(state, addDays(today, -1), addDays(today, 2));
    result.hiddenCalendarsCounted = availability.hiddenCalendarsCounted;
    result.uncertainCount = availability.uncertain.length;
    // Core treats point events as a 1 ms blocker. Reversed stored intervals must not imply freedom.
    malformed ||= Object.values(state.events).some((event) => !event.deleted && (
      reversedTiming(event.timing) || Object.values(event.overrides ?? {}).some((override) => !override.cancelled && override.timing && reversedTiming(override.timing))
    )) || Object.values(state.sourceItems).some((item) => !item.archived && reversedTiming(item.data.timing));
    const busy = availability.busy.map((interval) => ({
      start: Math.max(nowMs, interval.start),
      end: Math.min(dayEnd, interval.end),
    }));
    if (malformed || busy.some((interval) => !Number.isFinite(interval.start) || !Number.isFinite(interval.end))) {
      result.uncertainCount++;
      return result;
    }

    const intervals = busy.filter((interval) => interval.end > interval.start).sort((a, b) => a.start - b.start || a.end - b.end);
    const merged: Array<{ start: number; end: number }> = [];
    for (const interval of intervals) {
      const previous = merged[merged.length - 1];
      if (previous && interval.start <= previous.end) previous.end = Math.max(previous.end, interval.end);
      else merged.push({ ...interval });
    }
    const addGap = (start: number, end: number) => {
      // UI labels show whole minutes; never offer a start label that is already in the past.
      const roundedStart = Math.ceil(start / MINUTE_MS) * MINUTE_MS;
      if (end - roundedStart < MIN_GAP_MS) return;
      result.firstGap ??= { start: roundedStart, end };
      result.freeMinutes += Math.floor((end - roundedStart) / MINUTE_MS);
    };
    let cursor = nowMs;
    for (const interval of merged) {
      addGap(cursor, interval.start);
      cursor = interval.end;
    }
    addGap(cursor, dayEnd);
    return result;
  } catch {
    // An unreadable local/imported interval cannot support an availability claim.
    return { ...result, freeMinutes: 0, firstGap: null, uncertainCount: result.uncertainCount + 1 };
  }
}

